import { Alert, Button, Field, Input, Select } from '@dnx/ui';
import { formatBrPhoneInput } from '@dnx/domain';
import { useMutation, useQuery } from '@tanstack/react-query';
import { SendHorizontal, Smartphone } from 'lucide-react';
import { useState } from 'react';
import { api } from '../lib/api.js';

interface Channels { organizationId: string; clinicName: string; channels: { id: string; name: string; provider: string }[] }
interface Out { id: string; text: string; createdAt: string; status: string }
interface Local { id: string; text: string; createdAt: string }

/**
 * SOMENTE DEV: faz o papel do celular do paciente. Envia pelo mesmo caminho do webhook da
 * Evolution (payload idêntico) e mostra as respostas que a clínica "enviou" para este número.
 */
export function WhatsAppSimulatorPage() {
  const [slug, setSlug] = useState('clinica-demo');
  const [channelId, setChannelId] = useState('');
  const [name, setName] = useState('Juliana Rocha');
  const [phone, setPhone] = useState('(11) 97654-3210');
  const [text, setText] = useState('');
  const [adTitle, setAdTitle] = useState('');
  const [sent, setSent] = useState<Local[]>([]);

  const channels = useQuery({ queryKey: ['dev', 'channels', slug], queryFn: () => api<Channels>(`/dev/whatsapp/channels?clinica=${slug}`), retry: false });
  const chan = channelId || channels.data?.channels[0]?.id || '';
  const outbound = useQuery({
    queryKey: ['dev', 'out', channels.data?.organizationId, phone],
    queryFn: () => api<Out[]>(`/dev/whatsapp/outbound?organizationId=${channels.data!.organizationId}&phone=${encodeURIComponent(phone)}`),
    enabled: !!channels.data, refetchInterval: 1500, refetchIntervalInBackground: true, // fica numa janela ao lado do painel
  });
  const send = useMutation({
    mutationFn: (t: string) => api('/dev/whatsapp/inbound', { method: 'POST', body: { channelAccountId: chan, phone, name, text: t, adTitle: adTitle.trim() || undefined } }),
    onSuccess: (_r, t) => { setSent((s) => [...s, { id: `${Date.now()}`, text: t, createdAt: new Date().toISOString() }]); setText(''); },
  });

  const thread = [
    ...sent.map((m) => ({ ...m, mine: true })),
    ...(outbound.data ?? []).map((m) => ({ id: m.id, text: m.text, createdAt: m.createdAt, mine: false })),
  ].sort((a, b) => a.createdAt.localeCompare(b.createdAt));

  return (
    <div className="grid min-h-dvh place-items-center bg-[#e5ddd5] p-6 font-sans">
      <div className="grid w-full max-w-4xl gap-6 md:grid-cols-[1fr_380px]">
        <div className="rounded-xl border border-dashed border-warning bg-surface p-5">
          <p className="flex items-center gap-2 font-semibold text-warning"><Smartphone className="size-5" />Simulador de WhatsApp · só em desenvolvimento</p>
          <p className="mt-1 text-[13px] text-fg-muted">Você é o paciente. As mensagens entram no painel da clínica pelo mesmo webhook da Evolution API.</p>
          <div className="mt-5 grid gap-4">
            <Field label="Clínica (endereço)"><Input value={slug} onChange={(e) => setSlug(e.target.value)} /></Field>
            {channels.error && <Alert tone="danger">{channels.error.message}</Alert>}
            {channels.data && channels.data.channels.length === 0 && (
              <Alert tone="warning" title="Clínica sem canal de WhatsApp">
                Rode: <code>pnpm --filter @dnx/api channel:create --org {slug} --name "WhatsApp Recepção" --provider dev</code>
              </Alert>
            )}
            {channels.data && channels.data.channels.length > 0 && (
              <Field label={`Número da clínica (${channels.data.clinicName})`}>
                <Select value={chan} onChange={(e) => setChannelId(e.target.value)}>
                  {channels.data.channels.map((c) => <option key={c.id} value={c.id}>{c.name} · {c.provider}</option>)}
                </Select>
              </Field>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Seu nome no WhatsApp"><Input value={name} onChange={(e) => setName(e.target.value)} /></Field>
              <Field label="Seu celular"><Input value={phone} onChange={(e) => { setPhone(formatBrPhoneInput(e.target.value)); setSent([]); }} /></Field>
            </div>
            <Field label="Veio de anúncio? (opcional)" hint="Título do anúncio Click-to-WhatsApp. Vale para o primeiro contato.">
              <Input value={adTitle} onChange={(e) => setAdTitle(e.target.value)} placeholder="Ex.: Transplante capilar sem cortes" />
            </Field>
          </div>
        </div>

        <div className="flex h-[640px] flex-col overflow-hidden rounded-[32px] border-8 border-[#111] bg-[#efeae2] shadow-pop">
          <div className="bg-[#075e54] px-4 py-3 text-white">
            <p className="font-semibold">{channels.data?.clinicName ?? '—'}</p>
            <p className="text-[11px] opacity-80">WhatsApp da clínica</p>
          </div>
          <div className="flex-1 space-y-1.5 overflow-y-auto p-3">
            {thread.map((m) => (
              <div key={m.id} className={`flex ${m.mine ? 'justify-end' : 'justify-start'}`}>
                <p className={`max-w-[80%] rounded-lg px-2.5 py-1.5 text-[13.5px] text-[#111] shadow-sm ${m.mine ? 'bg-[#dcf8c6]' : 'bg-white'}`}>
                  {m.text}
                  <span className="ml-2 align-bottom text-[10px] text-[#667781]">
                    {new Date(m.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </p>
              </div>
            ))}
          </div>
          <form className="flex gap-2 bg-[#f0f2f5] p-2" onSubmit={(e) => { e.preventDefault(); if (text.trim() && chan) send.mutate(text.trim()); }}>
            <input aria-label="Mensagem do paciente" value={text} onChange={(e) => setText(e.target.value)} placeholder="Mensagem"
              className="h-10 flex-1 rounded-full bg-white px-4 text-sm text-[#111] outline-none" />
            <Button type="submit" size="icon" className="size-10 rounded-full bg-[#00a884] hover:bg-[#008f6f]" aria-label="Enviar" disabled={!text.trim() || !chan}>
              <SendHorizontal className="size-4" />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
